import { useState, useEffect } from "react";
import { X, CreditCard, IndianRupee, Wallet, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";

export interface PaymentFlowData {
  isAdvance: boolean;
  isSplit: boolean;
  paymentType: string; // 'Cash' | 'Online' | 'Split'
  splitPayments: Array<{
    paymentMethod: string;
    amount: number;
  }>;
}

interface PaymentSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalAmount: number;
  doctorName?: string;
  patientName?: string;
  slotTime?: string;
  date?: string;
  onConfirmBooking: (paymentData: PaymentFlowData) => void;
  isBookingLoading?: boolean;
}

export function PaymentSummaryModal({
  isOpen,
  onClose,
  totalAmount,
  doctorName,
  patientName,
  slotTime,
  date,
  onConfirmBooking,
  isBookingLoading = false,
}: PaymentSummaryModalProps) {
  const [paymentOption, setPaymentOption] = useState<"full" | "advance">("full");
  const [advanceAmount, setAdvanceAmount] = useState<number>(Math.round(totalAmount / 2) || 200);
  const [paymentType, setPaymentType] = useState<"cash" | "online" | "split">("cash");

  // Split details
  const [cashAmount, setCashAmount] = useState<number>(totalAmount);
  const [onlineAmount, setOnlineAmount] = useState<number>(0);

  // Recalculate default amounts when paymentOption or paymentType changes
  useEffect(() => {
    const requiredAmount = paymentOption === "advance" ? Number(advanceAmount) || 0 : totalAmount;
    if (paymentType === "cash") {
      setCashAmount(requiredAmount);
      setOnlineAmount(0);
    } else if (paymentType === "online") {
      setCashAmount(0);
      setOnlineAmount(requiredAmount);
    } else if (paymentType === "split") {
      const half = Math.round(requiredAmount / 2);
      setCashAmount(half);
      setOnlineAmount(requiredAmount - half);
    }
  }, [paymentOption, paymentType, totalAmount, advanceAmount]);

  if (!isOpen) return null;

  const targetPaymentAmount = paymentOption === "advance" ? Number(advanceAmount) || 0 : totalAmount;
  const currentTotalPaid = paymentType === "split"
    ? (Number(cashAmount) || 0) + (Number(onlineAmount) || 0)
    : targetPaymentAmount;

  const balanceDue = totalAmount - currentTotalPaid;

  const handleConfirm = () => {
    if (paymentOption === "advance") {
      if (!advanceAmount || advanceAmount <= 0) {
        toast.error("Please enter a valid advance amount");
        return;
      }
      if (advanceAmount >= totalAmount) {
        toast.error("Advance amount must be less than total fee");
        return;
      }
    }

    if (paymentType === "split") {
      const cash = Number(cashAmount) || 0;
      const online = Number(onlineAmount) || 0;
      if (cash < 0 || online < 0) {
        toast.error("Amount cannot be negative");
        return;
      }
      if (cash + online !== targetPaymentAmount) {
        toast.error(`Split payments must total ₹${targetPaymentAmount}`);
        return;
      }
    }

    // Build payload
    const isAdvance = paymentOption === "advance";
    const isSplit = paymentType === "split";

    let splitPayments: Array<{ paymentMethod: string; amount: number; }> = [];

    if (paymentType === "cash") {
      splitPayments = [{ paymentMethod: "cash", amount: targetPaymentAmount }];
    } else if (paymentType === "online") {
      splitPayments = [
        {
          paymentMethod: "online",
          amount: targetPaymentAmount,
        },
      ];
    } else {
      splitPayments = [
        { paymentMethod: "cash", amount: Number(cashAmount) || 0 },
        {
          paymentMethod: "online",
          amount: Number(onlineAmount) || 0,
        },
      ].filter((p) => p.amount > 0);
    }

    let finalPaymentType = "Cash";
    if (paymentType === "online") finalPaymentType = "Online";
    if (paymentType === "split") finalPaymentType = "Split";

    onConfirmBooking({
      isAdvance,
      isSplit,
      paymentType: finalPaymentType,
      splitPayments,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100">
        {/* Minimal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Payment & Booking Summary</h3>
            <p className="text-xs text-gray-500">Select payment details to complete booking</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors"
            disabled={isBookingLoading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Minimal Booking Details Card */}
          <div className="bg-gray-50/70 p-3.5 rounded-xl border border-gray-100 text-xs space-y-1.5">
            {patientName && (
              <div className="flex justify-between">
                <span className="text-gray-500">Patient:</span>
                <span className="font-semibold text-gray-900">{patientName}</span>
              </div>
            )}
            {doctorName && (
              <div className="flex justify-between">
                <span className="text-gray-500">Doctor:</span>
                <span className="font-semibold text-gray-900">{doctorName}</span>
              </div>
            )}
            {(date || slotTime) && (
              <div className="flex justify-between">
                <span className="text-gray-500">Slot:</span>
                <span className="font-medium text-gray-800">
                  {date} {slotTime ? `(${slotTime})` : ""}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-gray-200/80 pt-2 mt-1.5">
              <span className="font-medium text-gray-700">Consultation Fee:</span>
              <span className="font-bold text-emerald-700 text-sm">₹{totalAmount}</span>
            </div>
          </div>

          {/* 1. Payment Option */}
          <div className="space-y-2.5">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              1. Payment Option
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPaymentOption("full")}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs font-medium transition-all ${paymentOption === "full"
                  ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                  : "border-gray-200 hover:bg-gray-50 text-gray-700"
                  }`}
              >
                <CheckCircle2 className={`w-4 h-4 ${paymentOption === "full" ? "text-emerald-600" : "text-gray-400"}`} />
                Full Payment (₹{totalAmount})
              </button>
              <button
                type="button"
                onClick={() => setPaymentOption("advance")}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs font-medium transition-all ${paymentOption === "advance"
                  ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                  : "border-gray-200 hover:bg-gray-50 text-gray-700"
                  }`}
              >
                <Wallet className={`w-4 h-4 ${paymentOption === "advance" ? "text-emerald-600" : "text-gray-400"}`} />
                Advance Payment
              </button>
            </div>

            {paymentOption === "advance" && (
              <div className="mt-2 p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                <label className="block text-xs font-medium text-gray-700">
                  Advance Amount (₹)
                </label>
                <input
                  type="number"
                  min={1}
                  max={totalAmount - 1}
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(Number(e.target.value))}
                  placeholder="Enter advance amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
                />
                <p className="text-[11px] text-gray-500">
                  Remaining balance due will be ₹{totalAmount - (Number(advanceAmount) || 0)}
                </p>
              </div>
            )}
          </div>

          {/* 2. Payment Type */}
          <div className="space-y-2.5">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              2. Payment Type
            </label>
            <div className="grid grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={() => setPaymentType("cash")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-medium gap-1 transition-all ${paymentType === "cash"
                  ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                  : "border-gray-200 hover:bg-gray-50 text-gray-700"
                  }`}
              >
                <IndianRupee className="w-4 h-4 text-gray-600" />
                Cash
              </button>
              <button
                type="button"
                onClick={() => setPaymentType("online")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-medium gap-1 transition-all ${paymentType === "online"
                  ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                  : "border-gray-200 hover:bg-gray-50 text-gray-700"
                  }`}
              >
                <CreditCard className="w-4 h-4 text-gray-600" />
                Online
              </button>
              <button
                type="button"
                onClick={() => setPaymentType("split")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-medium gap-1 transition-all ${paymentType === "split"
                  ? "bg-emerald-50/60 border-emerald-600 text-emerald-800"
                  : "border-gray-200 hover:bg-gray-50 text-gray-700"
                  }`}
              >
                <Wallet className="w-4 h-4 text-gray-600" />
                Split
              </button>
            </div>
          </div>

          {/* Split Payment Form / Online Order Id */}
          {paymentType === "split" && (
            <div className="p-3.5 bg-gray-50/80 rounded-xl border border-gray-200 space-y-2.5">
              <h4 className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                Split Breakdown (Total Needed: ₹{targetPaymentAmount})
              </h4>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">
                    Cash Paid (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={cashAmount}
                    onChange={(e) => setCashAmount(Number(e.target.value))}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs bg-white focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-1">
                    Online Paid (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={onlineAmount}
                    onChange={(e) => setOnlineAmount(Number(e.target.value))}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs bg-white focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {cashAmount + onlineAmount !== targetPaymentAmount && (
                <p className="text-[11px] text-red-600 font-medium">
                  ⚠️ Cash (₹{cashAmount}) + Online (₹{onlineAmount}) = ₹{cashAmount + onlineAmount}. Expected: ₹{targetPaymentAmount}.
                </p>
              )}
            </div>
          )}
          {/* Clean Minimal Light Summary Box */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-xs space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Total Doctor Fee:</span>
              <span className="font-semibold text-gray-900">₹{totalAmount}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Payment Option:</span>
              <span className="font-medium text-gray-900 capitalize">{paymentOption} Payment</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Payment Method:</span>
              <span className="font-medium text-gray-900 capitalize">{paymentType}</span>
            </div>
            <div className="flex justify-between text-gray-900 border-t border-gray-200 pt-2 font-semibold">
              <span>Paying Now:</span>
              <span className="text-emerald-700 font-bold text-sm">₹{currentTotalPaid}</span>
            </div>
            <div className="flex justify-between text-gray-700 font-semibold">
              <span>Balance Due:</span>
              <span className="text-gray-900">₹{Math.max(0, balanceDue)}</span>
            </div>
          </div>
        </div>

        {/* Minimal Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          <button
            type="button"
            onClick={onClose}
            disabled={isBookingLoading}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isBookingLoading}
            className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
          >
            {isBookingLoading ? "Booking..." : "Confirm & Book Appointment"}
          </button>
        </div>
      </div>
    </div>
  );
}
